import { 
  Airport, 
  Machine, 
  CreateMachineDto, 
  CreateMediaDto,
  MachineReservation,
  CreateReservationRequest,
  ApproveReservationRequest,
  DenyReservationRequest,
  MachineTier,
} from '../types/map';

// Venue Type APIs
export interface VenueType {
  venueType: string;
  count: number;
}

export async function getVenueTypes(): Promise<VenueType[]> {
  const res = await fetch('/api/store/venue-types');
  if (!res.ok) throw new Error('Failed to fetch venue types');
  return res.json();
}

export async function getMachines(filters?: {
  venueType?: string;
  status?: string;
  availabilityType?: string;
  hasAvailableSkus?: boolean;
}): Promise<Machine[]> {
  const params = new URLSearchParams();
  if (filters?.venueType) params.append('venueType', filters.venueType);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.availabilityType) params.append('availabilityType', filters.availabilityType);
  if (filters?.hasAvailableSkus !== undefined) params.append('hasAvailableSkus', String(filters.hasAvailableSkus));
  
  const url = `/api/store/machines${params.toString() ? '?' + params.toString() : ''}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to fetch machines');
  return res.json();
}

// Deprecated: Use getVenueTypes and getMachines instead
export async function getAirports(): Promise<Airport[]> {
  const res = await fetch('/api/store/airports');
  if (!res.ok) throw new Error('Failed to fetch airports');
  return res.json();
}

// Deprecated: Use getMachines with filters instead
export async function getAirportMachines(code: string): Promise<Machine[]> {
  const res = await fetch(`/api/store/airports/${code}/machines`);
  if (!res.ok) throw new Error('Failed to fetch machines');
  return res.json();
}

export async function getMachine(slug: string): Promise<Machine> {
  const res = await fetch(`/api/store/machines/${slug}`);
  if (!res.ok) throw new Error('Failed to fetch machine');
  return res.json();
}

export async function createMachine(data: CreateMachineDto): Promise<Machine> {
  const res = await fetch('/api/admin/machines', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to create machine');
  return res.json();
}

export async function createMachineMedia(machineId: string, data: CreateMediaDto): Promise<void> {
  const res = await fetch(`/api/admin/machines/${machineId}/media`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to create media');
}

export async function updateMachine(id: string, data: Partial<CreateMachineDto>): Promise<Machine> {
  const res = await fetch(`/api/admin/machines/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ error: 'Failed to update machine' }));
    throw new Error(errorData.error || 'Failed to update machine');
  }
  return res.json();
}

export async function deleteMachine(id: string): Promise<void> {
  const res = await fetch(`/api/admin/machines/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error('Failed to delete machine');
}

export async function deleteMachineMedia(machineId: string, mediaId: string): Promise<void> {
  const res = await fetch(`/api/admin/machines/${machineId}/media/${mediaId}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error('Failed to delete media');
}

// Brand Reservation APIs
export async function createReservationRequest(data: CreateReservationRequest): Promise<{ success: boolean; reservations: number; message: string }> {
  const res = await fetch('/api/brand/reservations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || 'Failed to create reservation request');
  }
  return res.json();
}

export async function getBrandReservations(status?: string): Promise<MachineReservation[]> {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  
  const url = `/api/brand/reservations${params.toString() ? '?' + params.toString() : ''}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to fetch reservations');
  return res.json();
}

export async function getBrandReservation(id: string): Promise<MachineReservation> {
  const res = await fetch(`/api/brand/reservations/${id}`);
  if (!res.ok) throw new Error('Failed to fetch reservation');
  return res.json();
}

export async function cancelReservationRequest(id: string): Promise<{ success: boolean; message: string }> {
  const res = await fetch(`/api/brand/reservations/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error('Failed to cancel reservation');
  return res.json();
}

// Admin Reservation APIs
export async function getAdminReservations(filters?: {
  status?: string;
  brandId?: string;
  machineId?: string;
}): Promise<MachineReservation[]> {
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);
  if (filters?.brandId) params.append('brandId', filters.brandId);
  if (filters?.machineId) params.append('machineId', filters.machineId);
  
  const url = `/api/admin/reservations${params.toString() ? '?' + params.toString() : ''}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to fetch reservations');
  return res.json();
}

export async function getPendingReservations(): Promise<MachineReservation[]> {
  const res = await fetch('/api/admin/reservations/pending');
  if (!res.ok) throw new Error('Failed to fetch pending reservations');
  return res.json();
}

export async function deleteAdminReservation(id: string): Promise<{ success: boolean; message: string; deletedSkuAllocations?: number }> {
  const res = await fetch(`/api/admin/reservations/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || 'Failed to delete reservation');
  }
  return res.json();
}

export async function getReservationStats(): Promise<{ pending: number; approved: number; denied: number; total: number }> {
  const res = await fetch('/api/admin/reservations/stats');
  if (!res.ok) throw new Error('Failed to fetch reservation stats');
  return res.json();
}

export async function approveReservation(id: string, data: ApproveReservationRequest): Promise<MachineReservation> {
  const res = await fetch(`/api/admin/reservations/${id}/approve`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || 'Failed to approve reservation');
  }
  return res.json();
}

export async function denyReservation(id: string, data: DenyReservationRequest): Promise<MachineReservation> {
  const res = await fetch(`/api/admin/reservations/${id}/deny`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || 'Failed to deny reservation');
  }
  return res.json();
}

export async function getAdminReservation(id: string): Promise<MachineReservation> {
  const res = await fetch(`/api/admin/reservations/${id}`);
  if (!res.ok) throw new Error('Failed to fetch reservation');
  return res.json();
}

// Planogram APIs
export interface Planogram {
  id: string;
  name: string;
  config: string;
  description: string | null;
  machineCount?: number;
  createdAt: string;
  updatedAt: string;
}

export async function getPlanograms(): Promise<Planogram[]> {
  const res = await fetch('/api/admin/planograms');
  if (!res.ok) throw new Error('Failed to fetch planograms');
  return res.json();
}

export async function getPlanogram(id: string): Promise<Planogram> {
  const res = await fetch(`/api/admin/planograms/${id}`);
  if (!res.ok) throw new Error('Failed to fetch planogram');
  return res.json();
}

export async function createPlanogram(data: { name: string; config: string; description?: string | null }): Promise<Planogram> {
  const res = await fetch('/api/admin/planograms', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || 'Failed to create planogram');
  }
  return res.json();
}

export async function updatePlanogram(id: string, data: { name?: string; config?: string; description?: string | null }): Promise<Planogram> {
  const res = await fetch(`/api/admin/planograms/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || 'Failed to update planogram');
  }
  return res.json();
}

export async function deletePlanogram(id: string): Promise<void> {
  const res = await fetch(`/api/admin/planograms/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || 'Failed to delete planogram');
  }
}

// Machine Tier APIs
export async function getMachineTiers(venueName?: string): Promise<MachineTier[]> {
  const url = venueName 
    ? `/api/admin/machines/tiers?venueName=${encodeURIComponent(venueName)}`
    : '/api/admin/machines/tiers';
  const res = await fetch(url, {
    credentials: 'include',
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || 'Failed to fetch machine tiers');
  }
  return res.json();
}

export async function createMachineTier(data: { 
  name: string; 
  venueName: string; 
  planogramId?: string | null;
  displayOrder?: number;
}): Promise<MachineTier> {
  const res = await fetch('/api/admin/machines/tiers', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || 'Failed to create machine tier');
  }
  return res.json();
}
